/**
 * TagCombobox
 *
 * Input combobox inline para TAGS do contato: chips das selecionadas + um campo
 * que, ao digitar, abre um dropdown filtrando o catálogo. Se o texto não existir,
 * recomenda "Criar 'X'". Selecionar/criar adiciona como chip e o campo continua
 * focado para ir adicionando várias. Persiste a cada mudança (substitui o
 * conjunto) e funciona nos 2 inboxes (telefone normalizado, fan-out no backend).
 *
 * Autocontido: carrega catálogo + tags do contato sozinho.
 *
 * Props:
 *   - contactPhone
 *   - colors
 *   - onChange(tagObjects)  -> notifica o conjunto atual (opcional)
 *   - autoFocus             -> foca o input ao montar (opcional)
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Tag, Plus, X, Loader2, ChevronDown } from 'lucide-react';
import contactTagsService from '../../services/global/contactTagsService';

const TagCombobox = ({ contactPhone, colors, onChange, autoFocus = false }) => {
  const [catalog, setCatalog] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  // Carrega catálogo (independe do telefone — é do workspace) + tags atribuídas
  // (só quando há telefone; no criar-lead o número ainda pode estar vazio).
  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      contactTagsService.listTags(),
      contactPhone ? contactTagsService.getContactTags(contactPhone) : Promise.resolve([]),
    ])
      .then(([cat, attached]) => {
        if (!alive) return;
        setCatalog(cat || []);
        setSelectedIds((attached || []).map((t) => t.id));
      })
      .catch(() => { if (alive) setError('Não foi possível carregar as tags.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [contactPhone]);

  // Fecha o dropdown ao clicar fora
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => { if (autoFocus) inputRef.current?.focus(); }, [autoFocus]);

  const selectedTags = useMemo(
    () => selectedIds.map((id) => catalog.find((t) => t.id === id)).filter(Boolean),
    [selectedIds, catalog]
  );
  const q = query.trim().toLowerCase();
  // (t.name || '') — uma tag com name nulo/ausente (ex.: dado migrado do Kommo)
  // faria .toLowerCase() lançar e, sem isso, derrubaria a árvore inteira =>
  // tela preta ao abrir os "Dados do contato". Nunca confie no shape do catálogo.
  const suggestions = useMemo(
    () => catalog.filter((t) => !selectedIds.includes(t.id) && (!q || (t.name || '').toLowerCase().includes(q))),
    [catalog, selectedIds, q]
  );
  const exact = useMemo(() => catalog.find((t) => (t.name || '').toLowerCase() === q), [catalog, q]);
  const canCreate = q.length > 0 && !exact;

  const notify = (ids) => {
    onChange?.(ids.map((id) => catalog.find((t) => t.id === id)).filter(Boolean));
  };

  const persist = async (ids) => {
    // Sem telefone ainda (criar-lead antes de digitar o número): mantém local.
    if (!contactPhone) { setSelectedIds(ids); notify(ids); return; }
    setBusy(true);
    setError(null);
    const prev = selectedIds;
    setSelectedIds(ids); // otimista
    try {
      await contactTagsService.setContactTags(contactPhone, ids);
      notify(ids);
    } catch (err) {
      setSelectedIds(prev); // reverte
      setError(err?.response?.data?.detail || 'Erro ao salvar as tags.');
    } finally {
      setBusy(false);
    }
  };

  const addTag = (id) => {
    if (selectedIds.includes(id)) return;
    persist([...selectedIds, id]);
    setQuery('');
    inputRef.current?.focus();
  };

  const removeTag = (id) => persist(selectedIds.filter((x) => x !== id));

  const createAndAdd = async () => {
    const name = query.trim();
    if (!name || busy) return;
    setBusy(true);
    setError(null);
    try {
      const created = await contactTagsService.createTag(name);
      setCatalog((prev) => (prev.some((t) => t.id === created.id) ? prev : [...prev, created]));
      setQuery('');
      // persist usa o catálogo atualizado de forma assíncrona; monta a lista aqui
      const nextIds = selectedIds.includes(created.id) ? selectedIds : [...selectedIds, created.id];
      setSelectedIds(nextIds);
      if (contactPhone) await contactTagsService.setContactTags(contactPhone, nextIds);
      onChange?.([...selectedTags, created].filter((t, i, a) => a.findIndex((x) => x.id === t.id) === i));
      inputRef.current?.focus();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erro ao criar a tag.');
    } finally {
      setBusy(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (exact) addTag(exact.id);
      else if (canCreate) createAndAdd();
      else if (suggestions.length === 1) addTag(suggestions[0].id);
    } else if (e.key === 'Backspace' && !query && selectedIds.length) {
      removeTag(selectedIds[selectedIds.length - 1]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={boxRef} style={{ width: '100%', position: 'relative', textAlign: 'left' }}>
      {/* Caixa de chips + input */}
      <div
        onClick={() => { setOpen(true); inputRef.current?.focus(); }}
        style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6,
          padding: '6px 8px', minHeight: 38, cursor: 'text',
          border: `1px solid ${open ? colors.accentPrimary : colors.borderColor}`,
          borderRadius: 10, backgroundColor: colors.bgPrimary,
        }}
      >
        <Tag size={14} color={colors.textTertiary} style={{ flexShrink: 0 }} />
        {selectedTags.map((t) => (
          <span key={t.id} style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            backgroundColor: `${t.color}22`, color: t.color,
            fontSize: 12, fontWeight: 600, padding: '3px 8px', borderRadius: 999,
          }}>
            {t.name}
            <button
              onClick={(e) => { e.stopPropagation(); removeTag(t.id); }}
              title="Remover" aria-label={`Remover ${t.name}`}
              style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, display: 'inline-flex' }}
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={query}
          disabled={loading}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={selectedTags.length ? 'Adicionar tag…' : 'Buscar ou criar tag…'}
          style={{
            flex: 1, minWidth: 110, border: 'none', outline: 'none',
            background: 'transparent', color: colors.textPrimary, fontSize: 13,
            padding: '2px 0', fontFamily: 'inherit',
          }}
        />
        {busy ? (
          <Loader2 size={14} color={colors.textTertiary} style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} />
        ) : (
          <ChevronDown size={14} color={colors.textTertiary} style={{ flexShrink: 0 }} />
        )}
      </div>

      {/* Dropdown */}
      {open && !loading && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 50,
          maxHeight: 240, overflowY: 'auto',
          backgroundColor: colors.bgPrimary, border: `1px solid ${colors.borderColor}`,
          borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', padding: 4,
        }}>
          {canCreate && (
            <button
              onClick={createAndAdd}
              disabled={busy}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '9px 10px', border: 'none', borderRadius: 8,
                background: 'transparent', cursor: 'pointer', textAlign: 'left',
                color: colors.accentPrimary, fontSize: 13, fontWeight: 600,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${colors.accentPrimary}10`; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <Plus size={14} /> Criar “{query.trim()}”
            </button>
          )}
          {suggestions.map((t) => (
            <button
              key={t.id}
              onClick={() => addTag(t.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                padding: '9px 10px', border: 'none', borderRadius: 8,
                background: 'transparent', cursor: 'pointer', textAlign: 'left',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.bgSecondary; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <span style={{ width: 12, height: 12, borderRadius: 4, backgroundColor: t.color, flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: colors.textPrimary }}>{t.name}</span>
            </button>
          ))}
          {!canCreate && suggestions.length === 0 && (
            <div style={{ padding: '10px', fontSize: 12, color: colors.textTertiary }}>
              {catalog.length === 0 ? 'Digite para criar a primeira tag.' : 'Nenhuma tag encontrada.'}
            </div>
          )}
        </div>
      )}

      {error && (
        <div style={{ marginTop: 6, fontSize: 11, color: '#DC2626' }}>{error}</div>
      )}
    </div>
  );
};

export default TagCombobox;
